/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * clipBasketDraft.js — durable local draft store for Upload Basket v2.
 *
 * P0 May 21, 2026.
 *
 * One source of truth model:
 *   • Local draft is authoritative the instant the picker returns.
 *   • Backend basket session is created AFTER the local draft exists.
 *   • Hydration from backend NEVER overwrites a fresh local row —
 *     it merges by `rowId`, leaving local fields untouched.
 *   • Rows survive route changes, refresh, modal unmount.
 *   • `File` handles cannot be persisted; on reload, rows are
 *     restored with `status="needs_repick"` and the user can
 *     re-attach files by re-picking (matched by fingerprint).
 *
 * One active draft per user/session. Storage key versioned.
 */

const STORAGE_KEY_PREFIX = "ndg:upload-basket-v2:";

function _safe() {
  try { return typeof localStorage !== "undefined" ? localStorage : null; }
  catch (_) { return null; }
}

function _key(userId) {
  return `${STORAGE_KEY_PREFIX}${userId || "anon"}`;
}

export function newDraftId() {
  return `v2-draft-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

export function newRowId() {
  return `row-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Empty draft skeleton — used both for first-pick and as the reset
 * shape after a successful basket handoff.
 */
export function emptyDraft({ draftId = null, intent = null } = {}) {
  const now = new Date().toISOString();
  return {
    schemaVersion: 2,
    draftId: draftId || newDraftId(),
    backendBasketId: null,
    intent: intent || null,
    confirmed: false,
    rows: [],
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Save draft to localStorage. Updates `updatedAt`. Returns the saved
 * draft. NEVER throws — storage failures degrade silently so the UI
 * never blocks on persistence.
 */
export function saveDraft(userId, draft) {
  const ss = _safe();
  if (!ss) return draft;
  try {
    const stamped = { ...draft, updatedAt: new Date().toISOString() };
    // Strip non-serialisable File handles before persisting.
    stamped.rows = (stamped.rows || []).map((r) => {
      const { file, _file, ...rest } = r;
      void file; void _file; // file handles are intentionally dropped
      return rest;
    });
    ss.setItem(_key(userId), JSON.stringify(stamped));
    return stamped;
  } catch (_) {
    return draft;
  }
}

/** Load draft (or null). */
export function loadDraft(userId) {
  const ss = _safe();
  if (!ss) return null;
  try {
    const raw = ss.getItem(_key(userId));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || obj.schemaVersion !== 2) return null;
    return obj;
  } catch (_) {
    return null;
  }
}

/** Drop the draft entirely. */
export function clearDraft(userId) {
  const ss = _safe();
  if (!ss) return;
  try { ss.removeItem(_key(userId)); } catch (_) { /* noop */ }
}

/**
 * Build a row object from a freshly-picked File. Includes a runtime
 * `file` field that we DO NOT persist — used by the upload pipeline
 * within the same React lifecycle.
 *
 * P0 May 22, 2026 — rows are ALWAYS created in `local_staged` state.
 * A size-0 File typically means iOS Photos is still downloading the
 * clip from iCloud; the row carries a soft `warning` string so the
 * UI can flag it, but it MUST NOT be hard-failed at intake. Upload
 * time re-validates size and either proceeds or surfaces a clear
 * "open Photos to finish download" instruction.
 */
export function rowFromFile(file) {
  const sizeZero = !file?.size;
  return {
    rowId: newRowId(),
    fingerprint: {
      name: file?.name || "(no name)",
      size: file?.size || 0,
      lastModified: file?.lastModified || 0,
      type: file?.type || null,
    },
    status: "local_staged",
    warning: sizeZero
      ? "Still preparing — Photos may still be downloading this clip from iCloud."
      : null,
    error: null,
    uploadId: null,
    progressPct: 0,
    totalBytes: file?.size || 0,
    uploadedBytes: 0,
    // Runtime-only; stripped on save.
    file,
  };
}

/**
 * Merge backend hydration into local draft — local fields ALWAYS win.
 * Used after `POST /api/clip-basket/create` returns the backend basket
 * id; we keep every local row intact and only learn the backend id.
 */
export function mergeBackendHydration(draft, backendBasketId) {
  return {
    ...draft,
    backendBasketId: backendBasketId || draft.backendBasketId,
  };
}

/**
 * Match a freshly-picked file against an existing local row by
 * fingerprint. Used after a reload: if the user re-picks the same
 * clips, we can re-attach the File handle to the existing rowId
 * without spawning duplicates.
 */
export function findRowByFingerprint(draft, file) {
  if (!draft || !draft.rows || !file) return null;
  for (const r of draft.rows) {
    const fp = r.fingerprint || {};
    if (
      fp.name === file.name &&
      fp.size === file.size &&
      fp.lastModified === file.lastModified
    ) {
      return r;
    }
  }
  return null;
}

/**
 * Stage raw File handles SYNCHRONOUSLY into the durable draft.
 *
 * P0 May 21, 2026 — called from MobilePhotosIntake.onChange. The
 * persistence write (localStorage.setItem) MUST complete in the
 * same JS tick as the picker's onChange firing, so a WKWebView
 * eviction between onChange and the next React commit cannot
 * lose the staged rows.
 *
 * No metadata. No thumbnails. No duration probing. No upload prep.
 * Just durable rows.
 *
 * Returns { draft, added, reattached } so the caller can update
 * React state from the same authoritative shape.
 */
export function stageRawFilesSync(userId, files, currentDraft) {
  const base = currentDraft || loadDraft(userId) || emptyDraft();
  const next = { ...base, rows: [...(base.rows || [])] };
  let added = 0;
  let reattached = 0;
  for (const f of files) {
    if (!f) continue;
    const existing = findRowByFingerprint(next, f);
    if (existing && existing.status !== "uploaded") {
      // Re-attach the runtime File handle to the existing rowId.
      // P0 May 22, 2026 — size:0 is a soft warning, not a failure.
      existing.file = f;
      existing.status = "local_staged";
      existing.warning = !f.size
        ? "Still preparing — Photos may still be downloading this clip from iCloud."
        : null;
      existing.error = null;
      // P0 May 24, 2026 — Forensic fix for Pass-1 R2-native bypass.
      // Re-picking the same iPhone clip is fresh operator intent. ANY
      // persisted upload-authority metadata from a prior session (e.g.
      // a pre-Pass-1 chunked attempt that wrote `uploadPath: "chunked"`
      // + `uploadId` to localStorage) MUST be cleared, or routing in
      // `ClipBasketV2.uploadRow` will dispatch the row to chunkedUpload
      // (the legacy local-disk relay) instead of cloudUpload (R2).
      // Symptom we are killing: iPhone re-picks IMG_xxxx.MOV →
      // fingerprint matches stale row → DiskExhaustedError toast
      // "Preview storage is full" from the chunked path.
      existing.uploadPath = undefined;
      existing.uploadId = null;
      existing.resumeFromChunk = null;
      existing.totalChunks = null;
      existing.progressPct = 0;
      existing.uploadedBytes = 0;
      reattached += 1;
      continue;
    }
    next.rows.push(rowFromFile(f));
    added += 1;
  }
  // SYNCHRONOUS write — happens before this function returns and
  // before any React state update / re-render takes place.
  saveDraft(userId, next);
  return { draft: next, added, reattached };
}
